import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Home as HomeIcon, IndianRupee } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const FALLBACK_IMG = 'https://images.unsplash.com/photo-1649989184479-73ab3b750a73?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBpbmRpYW4lMjBhcGFydG1lbnQlMjBleHRlcmlvciUyMGJ1aWxkaW5nJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHxibHVlfDE3ODk2Mzg3NTV8MA&ixlib=rb-4.1.0&q=85';

const formatPrice = (price) => {
  if (!price) return 'N/A';
  if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
  if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
  return `₹${price.toLocaleString('en-IN')}`;
};

const statusBadge = {
  new_launch: { label: 'New Launch', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  under_construction: { label: 'Under Construction', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  ready_to_move: { label: 'Ready to Move', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  resale: { label: 'Resale', bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' }
};

const PropertyCard = ({ project }) => {
  const status = statusBadge[project.project_status] || statusBadge.new_launch;

  return (
    <Link to={`/project/${project.id}`} className="property-card block bg-white rounded-lg border border-slate-200 overflow-hidden hover:border-slate-300 transition-shadow" data-testid={`property-card-${project.id}`}>
      <div className="relative h-48 bg-slate-100">
        <img
          src={project.cover_image_url || FALLBACK_IMG}
          alt={project.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-md border ${status.bg} ${status.text} ${status.border}`}>
            {status.label}
          </span>
        </div>
        {project.rera_number && (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-md">
              <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
              RERA
            </span>
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="font-bold text-slate-900 text-base line-clamp-1">{project.name}</h3>
        <div className="flex items-center gap-1 text-slate-500 text-xs mt-1">
          <MapPin size={12} />
          <span>{project.locality}, {project.city}</span>
        </div>

        {project.builder_name && (
          <div className="text-xs text-slate-600 mt-2">by <span className="font-semibold">{project.builder_name}</span></div>
        )}

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {project.configurations?.slice(0, 3).map((cfg, i) => (
            <span key={i} className="text-xs font-medium text-slate-700 bg-slate-100 px-2 py-0.5 rounded">{cfg}</span>
          ))}
        </div>

        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500">Starting from</div>
            <div className="font-bold text-[#0F2C59] text-base">{formatPrice(project.price_min)}</div>
          </div>
          <button className="text-xs font-semibold text-[#F97316] hover:text-[#EA580C]">View Details →</button>
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;
export { formatPrice };
